/**
 * src/pages/Dashboard.jsx
 * Home: KPIs del sistema, resumen de clientes y estado del scheduler.
 */
import { useNavigate } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { Header } from '../components/layout/Header'
import { KpiCard, Card } from '../components/ui/Card'
import { Badge, TIER_COLOR } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { useClientes } from '../hooks/useClientes'
import { useSchedulerJobs } from '../hooks/useScraping'

function ClienteRow({ cliente, onNavigate }) {
  return (
    <div
      onClick={() => onNavigate(`/clientes/${cliente.cliente_id}`)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '11px 0',
        borderBottom: '1px solid var(--color-border)',
        cursor: 'pointer', transition: 'opacity 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
      onMouseLeave={e => e.currentTarget.style.opacity = '1'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '7px',
          background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '13px', fontWeight: 600, color: 'var(--color-accent)',
          fontFamily: 'var(--font-mono)',
        }}>
          {cliente.nombre.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text)' }}>
            {cliente.nombre}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
            {cliente.familia_cm} · {cliente.cliente_id}
          </div>
        </div>
      </div>
      <Badge color={TIER_COLOR[cliente.tier] || 'gray'}>
        {cliente.tier}
      </Badge>
    </div>
  )
}

// Datos de ejemplo para el gráfico (en producción vendría de la API)
function RunsChart({ clienteCount }) {
  const days = ['L', 'M', 'X', 'J', 'V', 'S', 'D']
  const data = days.map((d, i) => ({
    name: d,
    runs: Math.floor(Math.random() * clienteCount * 2 + 1),
  }))

  return (
    <ResponsiveContainer width="100%" height={100}>
      <BarChart data={data} margin={{ top: 4, right: 0, left: -20, bottom: 0 }}>
        <XAxis
          dataKey="name"
          tick={{ fill: 'var(--color-text-3)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
          axisLine={false} tickLine={false}
        />
        <YAxis hide />
        <Tooltip
          contentStyle={{
            background: 'var(--color-surface)', border: '1px solid var(--color-border)',
            borderRadius: '6px', fontSize: '12px', fontFamily: 'var(--font-mono)',
            color: 'var(--color-text)',
          }}
          cursor={{ fill: 'rgba(16,185,129,0.05)' }}
        />
        <Bar dataKey="runs" radius={[3, 3, 0, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={i === days.length - 1 ? 'var(--color-accent)' : 'var(--color-border-2)'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const { data: clientesRaw, isLoading } = useClientes()
  const clientes = Array.isArray(clientesRaw) ? clientesRaw : []
  const { data: schedulerData } = useSchedulerJobs()

  const activeJobs = schedulerData?.jobs?.length ?? 0
  const totalClientes = clientes.length

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Dashboard"
        subtitle={new Date().toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        action={
          <Button
            size="sm" variant="secondary"
            icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
            onClick={() => navigate('/clientes')}
          >
            Nuevo cliente
          </Button>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        {/* KPIs */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px',
          marginBottom: '24px',
        }}>
          <KpiCard
            label="CLIENTES ACTIVOS"
            value={isLoading ? '—' : totalClientes}
            sub={isLoading ? 'Cargando...' : `${clientes.filter(c => c.tier === 'premium').length} premium`}
          />
          <KpiCard
            label="JOBS SCHEDULER"
            value={activeJobs}
            color={activeJobs > 0 ? 'var(--color-accent)' : 'var(--color-text-3)'}
            sub={activeJobs > 0 ? 'ejecutándose' : 'sin jobs activos'}
          />
          <KpiCard
            label="FAMILIAS CM"
            value={isLoading ? '—' : new Set(clientes.map(c => c.familia_cm)).size}
            sub="familias configuradas"
            color="var(--color-running)"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          {/* Clientes */}
          <Card
            title="Clientes"
            subtitle={`${totalClientes} registros`}
            action={
              <Button size="sm" variant="ghost" onClick={() => navigate('/clientes')}>
                Ver todos →
              </Button>
            }
          >
            {isLoading ? (
              <div style={{
                textAlign: 'center', padding: '24px',
                color: 'var(--color-text-3)', fontSize: '12px', fontFamily: 'var(--font-mono)',
              }}>
                Cargando...
              </div>
            ) : clientes.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '24px',
                color: 'var(--color-text-3)', fontSize: '12px', fontFamily: 'var(--font-mono)',
              }}>
                Sin clientes — crea el primero
              </div>
            ) : (
              clientes.slice(0, 5).map(c => (
                <ClienteRow key={c.id} cliente={c} onNavigate={navigate} />
              ))
            )}
          </Card>

          {/* Actividad */}
          <Card title="Actividad (7 días)" subtitle="Runs del pipeline">
            {totalClientes > 0 ? (
              <RunsChart clienteCount={totalClientes} />
            ) : (
              <div style={{
                textAlign: 'center', padding: '24px',
                color: 'var(--color-text-3)', fontSize: '12px', fontFamily: 'var(--font-mono)',
              }}>
                Sin datos de actividad
              </div>
            )}

            <div style={{
              marginTop: '16px', paddingTop: '14px',
              borderTop: '1px solid var(--color-border)',
            }}>
              <div style={{
                fontSize: '11px', color: 'var(--color-text-3)',
                fontFamily: 'var(--font-mono)', letterSpacing: '0.06em', marginBottom: '8px',
              }}>
                SCHEDULER
              </div>
              {activeJobs === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
                  Sin jobs activos
                </div>
              ) : (
                schedulerData?.jobs?.map(job => (
                  <div key={job.id} style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: '12px', color: 'var(--color-text-2)',
                    fontFamily: 'var(--font-mono)', marginBottom: '4px',
                  }}>
                    <span>{job.id}</span>
                    <span style={{ color: 'var(--color-text-3)' }}>
                      {job.next_run ? new Date(job.next_run).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
